import React from "react";
import {
  Box,
  FormControl,
  FormHelperText,
  FormLabel,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import { useTheme } from "@material-ui/styles";
import { Controller, useForm } from "react-hook-form";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import Text from "../../../../../shared/components/Text";
import IconTitle from "../../../../../shared/components/IconTitle";
import MainButton from "../../../../../shared/components/MainButton";
import {
  infoDataFamilyForm,
  maritalRegimeTypesConfig,
  maritalStatusThatAllowsMaritalRegime,
  maritalStatusTypesConfig,
} from "../../config";
import { objectTypesConfigToArray } from "../../../../../../utils/objectTypesConfigToArray";
import InfoPanel from "../../../../../shared/components/InfoPanel";
import { ICON_TITLE_HEIGHT } from "../../../../../../config/config";
import { assetIds, ThemedAsset } from "nemuru-components";

export const useToggleButtonStyles = (buttonsAmount) =>
  makeStyles((theme) => ({
    toggleButtonroot: {
      width: `calc(100% / ${buttonsAmount})`,
      backgroundColor: theme.palette.grey[100],
      color: theme.palette.text.primary,
      border: "none",
      textTransform: "none",
    },
    toggleButtonselected: {
      backgroundColor: `${theme.palette.text.primary} !important`,
      color: `${theme.palette.common.white} !important`,
    },
  }));

export default function AdditionalDataFamilyForm({
  onSubmitForm,
  data,
  disableMaritalRegime,
}) {
  const { palette, shape } = useTheme();
  const classes = useToggleButtonStyles(5)();

  const { register, getValues, handleSubmit, errors, control, watch } = useForm(
    {
      defaultValues: {
        maritalStatus: data?.maritalStatus || "",
        maritalRegime: data?.maritalRegime || "",
        childSupportNumber: data?.childSupportNumber || "",
      },
    }
  );

  const maritalRegimeIsAllowed =
    watch("maritalStatus") === maritalStatusThatAllowsMaritalRegime;

  return (
    <>
      <Box className="appear-anim">
        <IconTitle
          icon={
            <ThemedAsset
              id={assetIds.ICON_ID_FAMILY_SITUATION}
              width={ICON_TITLE_HEIGHT}
              height={ICON_TITLE_HEIGHT}
              style={{ color: palette.secondary.main }}
            />
          }
          title={<Text tId="ADDITIONAL_INFORMATION" />}
          subtitle={<Text tId="FAMILY_SITUATION" />}
        />
        <FormControl fullWidth>
          <InputLabel id="maritalStatus">
            <Text tId="MARITAL_STATUS" />
          </InputLabel>
          <Controller
            name="maritalStatus"
            control={control}
            rules={{
              required: {
                value: true,
                message: <Text tId={"REQUIRED_FIELD"} />,
              },
            }}
            render={(props) => (
              <>
                <Select
                  MenuProps={{
                    MenuListProps: {
                      "aria-label": "Select marital status",
                    },
                  }}
                  onChange={(e) => props.onChange(e.target.value)}
                  value={props.value}
                  error={errors.maritalStatus ? true : false}
                >
                  {objectTypesConfigToArray(maritalStatusTypesConfig).map(
                    (type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.display}
                      </MenuItem>
                    )
                  )}
                </Select>
                <FormHelperText style={{ color: palette.error.main }}>
                  {errors.maritalStatus?.message}
                </FormHelperText>
              </>
            )}
          />
        </FormControl>

        {disableMaritalRegime !== true && maritalRegimeIsAllowed && (
          <FormControl fullWidth>
            <InputLabel id="maritalRegime">
              <Text tId="MARITAL_REGIME" />
            </InputLabel>
            <Controller
              name="maritalRegime"
              control={control}
              rules={{
                required: {
                  value: maritalRegimeIsAllowed,
                  message: <Text tId={"REQUIRED_FIELD"} />,
                },
              }}
              render={(props) => (
                <>
                  <Select
                    MenuProps={{
                      MenuListProps: {
                        "aria-label": "Select marital regime",
                      },
                    }}
                    onChange={(e) => props.onChange(e.target.value)}
                    value={props.value}
                    error={errors.maritalRegime ? true : false}
                  >
                    {objectTypesConfigToArray(maritalRegimeTypesConfig).map(
                      (type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.display}
                        </MenuItem>
                      )
                    )}
                  </Select>
                  <FormHelperText style={{ color: palette.error.main }}>
                    {errors.maritalRegime?.message}
                  </FormHelperText>
                </>
              )}
            />
          </FormControl>
        )}
        <FormControl fullWidth>
          <Box display="flex" alignItems="flex-end" mb={2}>
            <Box flexGrow={1}>
              <FormLabel
                id="childSupportNumber"
                style={{ color: palette.text.primary }}
              >
                <Typography variant="body2">
                  <Text tId="CHILD_SUPPORT_NUMBER" />
                </Typography>
              </FormLabel>
            </Box>
            <Box>
              <TextField
                type="number"
                id="childSupportNumber"
                name="childSupportNumber"
                style={{
                  width: "50px",
                  margin: 0,
                }}
                error={errors.childSupportNumber ? true : false}
                inputProps={{
                  "aria-label": "child support number",
                  ref: register({
                    required: {
                      value: true,
                      message: <Text tId={"REQUIRED_FIELD"} />,
                    },
                  }),
                }}
              />
              <FormHelperText style={{ color: palette.error.main }}>
                {errors.childSupportNumber?.message}
              </FormHelperText>
            </Box>
          </Box>
          <Controller
            name="childSupportNumber"
            control={control}
            rules={{
              required: {
                value: true,
                message: <Text tId={"REQUIRED_FIELD"} />,
              },
            }}
            render={(props) => (
              <ToggleButtonGroup
                style={{
                  width: "100%",
                  borderRadius: shape.borderRadius,
                }}
                value={watch("childSupportNumber")}
                onChange={(e) => props.onChange(e.target.value)}
                exclusive
                aria-label="text alignment"
              >
                <ToggleButton
                  value="0"
                  classes={{
                    root: classes.toggleButtonroot,
                    selected: classes.toggleButtonselected,
                  }}
                >
                  <div style={{ pointerEvents: "none" }}>0</div>
                </ToggleButton>
                <ToggleButton
                  value="1"
                  classes={{
                    root: classes.toggleButtonroot,
                    selected: classes.toggleButtonselected,
                  }}
                >
                  1
                </ToggleButton>
                <ToggleButton
                  value="2"
                  classes={{
                    root: classes.toggleButtonroot,
                    selected: classes.toggleButtonselected,
                  }}
                >
                  2
                </ToggleButton>
                <ToggleButton
                  value="3"
                  classes={{
                    root: classes.toggleButtonroot,
                    selected: classes.toggleButtonselected,
                  }}
                >
                  3
                </ToggleButton>
                <ToggleButton
                  value="4"
                  classes={{
                    root: classes.toggleButtonroot,
                    selected: classes.toggleButtonselected,
                  }}
                >
                  4
                </ToggleButton>
              </ToggleButtonGroup>
            )}
          />
        </FormControl>
        {maritalRegimeIsAllowed && <InfoPanel infoData={infoDataFamilyForm} />}
      </Box>
      <Box>
        <MainButton onClick={handleSubmit(() => onSubmitForm(getValues()))} />
      </Box>
    </>
  );
}
